/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-scroll";
import logo from "../images/logo.png";
import "./Navbar.css";

function Navbar({ audioRef, onBlogClick, onCreditsClick, onTeamClick, onVoyageClick }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(-120);
  const navbarRef = useRef(null);

  // Audio controls
  const [isPlaying, setIsPlaying] = useState(false);

  // Set initial play state based on audio element
  useEffect(() => {
    if (!audioRef?.current) return;
    const audio = audioRef.current;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    // Set initial state
    setIsPlaying(!audio.paused);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [audioRef]);

  const handlePlay = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleMenuToggle = () => {
    console.log("Menu toggle clicked, current state:", isMenuOpen);
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCloseMenu = () => {
    console.log("Closing menu");
    setIsMenuOpen(false);
  };

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolling = window.scrollY > 0;
      setIsSticky(isScrolling);
    };

    const handleResize = () => {
      calculateOffset();
    };

    const calculateOffset = () => {
      if (navbarRef.current) {
        const navbarHeight = navbarRef.current.offsetHeight;
        setScrollOffset(-navbarHeight - 20); // Add some padding
      }
    };

    // Initial calculation
    calculateOffset();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <nav className={`navbar${isSticky ? " sticky" : ""}`} ref={navbarRef}>
      <audio ref={audioRef} src={`${process.env.PUBLIC_URL}/ambient.mp3`} loop preload="auto" />
      <section className={`Navbar${isSticky ? " sticky" : ""}`}>
        <nav className="navbar navbar-expand-lg">
          <div className="container-fluid">
            <div className="navbar-brand-section">
              <img src={logo} alt="Logo" className="navbar-brand" />
              {/* Audio Controls */}
              <div className="audio-controls">
                <button onClick={handlePlay} disabled={isPlaying} className="audio-play-btn">
                  ▶
                </button>
                <button onClick={handlePause} disabled={!isPlaying} className="audio-pause-btn">
                  ❚❚
                </button>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="navbar-collapse" id="navbarSupportedContent">
              <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
                <li className="nav-item">
                  <Link to="top" spy={true} smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    Home
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="about" spy={true} smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    About
                  </Link>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className="nav-link nav-button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof onVoyageClick === "function") onVoyageClick();
                    }}
                  >
                    Voyage
                  </button>
                </li>

                <li className="nav-item">
                  <Link
                    to="collection"
                    spy={true}
                    smooth={true}
                    duration={500}
                    offset={scrollOffset}
                    className="nav-link"
                  >
                    Genesis
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="realms" spy={true} smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    Realms
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="qna" spy={true} smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    FAQ
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="contact-section" smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    Contact
                  </Link>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className="nav-link nav-button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof onBlogClick === "function") onBlogClick();
                    }}
                  >
                    Blog
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className="nav-link nav-button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof onTeamClick === "function") onTeamClick();
                    }}
                  >
                    Meet
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className="nav-link nav-button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof onCreditsClick === "function") onCreditsClick();
                    }}
                  >
                    Credits
                  </button>
                </li>
                <li className="nav-item">
                  <Link to="footer" spy={true} smooth={true} duration={500} offset={scrollOffset} className="nav-link">
                    Join
                  </Link>
                </li>
              </ul>
            </div>

            <button
              className="navbar-toggler"
              type="button"
              aria-controls="navbarText"
              aria-expanded={isMenuOpen ? "true" : "false"}
              onClick={handleMenuToggle}
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Mobile Menu Backdrop */}
            {isMenuOpen &&
              createPortal(
                <>
                  <div className="mobile-menu-backdrop" onClick={handleCloseMenu}></div>
                  <div className={`mobile-menu ${isMenuOpen ? "show" : ""}`}>
                    <div className="mobile-menu-header">
                      <h5 className="mobile-menu-title">Menu</h5>
                      <button type="button" className="mobile-menu-close" onClick={handleCloseMenu}>
                        <span>&times;</span>
                      </button>
                    </div>
                    <div className="mobile-menu-body">
                      <ul className="mobile-nav-list">
                        {/* Add Home as the first nav-item */}
                        <li className="mobile-nav-item">
                          <Link
                            to="top"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            Home
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <Link
                            to="about"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            About
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <button
                            type="button"
                            className="mobile-nav-link mobile-nav-button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (typeof onVoyageClick === "function") onVoyageClick();
                              handleCloseMenu();
                            }}
                          >
                            Voyage Map
                          </button>
                        </li>

                        <li className="mobile-nav-item">
                          <Link
                            to="collection"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            Genesis
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <Link
                            to="realms"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            Realms
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <Link
                            to="qna"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            FAQ
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <Link
                            to="contact-section"
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            Contact
                          </Link>
                        </li>
                        <li className="mobile-nav-item">
                          <button
                            type="button"
                            className="mobile-nav-link mobile-nav-button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (typeof onBlogClick === "function") onBlogClick();
                              handleCloseMenu();
                            }}
                          >
                            Blog
                          </button>
                        </li>
                        <li className="mobile-nav-item">
                          <button
                            type="button"
                            className="mobile-nav-link mobile-nav-button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (typeof onTeamClick === "function") onTeamClick();
                              handleCloseMenu();
                            }}
                          >
                            Meet
                          </button>
                        </li>
                        <li className="mobile-nav-item">
                          <button
                            type="button"
                            className="mobile-nav-link mobile-nav-button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (typeof onCreditsClick === "function") onCreditsClick();
                              handleCloseMenu();
                            }}
                          >
                            Credits
                          </button>
                        </li>
                        <li className="mobile-nav-item">
                          <Link
                            to="footer"
                            spy={true}
                            smooth={true}
                            duration={500}
                            offset={scrollOffset}
                            onClick={handleCloseMenu}
                            className="mobile-nav-link"
                          >
                            Join
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </>,
                document.body
              )}
          </div>
        </nav>
      </section>
    </nav>
  );
}

export default Navbar;
